/**
 * The crew board — `design/mockups/06-scheduling/scheduling-main.png`.
 *
 * Queue on the left, crews and days in the middle, the selected item's best
 * fits on the right. The week is in the URL, so a board someone is looking at
 * is a board they can send.
 *
 * It listens on the event stream: two people schedule at once, and the second
 * one needs to see the first one's visit rather than drop a crew onto a day
 * that filled up while they were reading it (§9's Phase 3 checkpoint).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { MeResponse } from '@rowted/shared'
import { PageBar } from '../frame/page-bar.js'
import { CrewGrid, type DragPayload } from '../components/crew-grid.js'
import { UnscheduledQueue } from '../components/unscheduled-queue.js'
import { BestFitPanel } from '../components/best-fit-panel.js'
import { Button, EmptyState, Segmented } from '../components/primitives.js'
import { Modal, Toast } from '../components/overlays.js'
import { IconChevronLeft, IconChevronRight } from '../frame/icons.js'
import { formatNumber, parseDateOnly } from '../lib/format.js'
import { request } from '../lib/api.js'
import type { BestFit, BoardVisit, BoardView, QueueItem } from '../lib/schedule-types.js'

/** Monday of the week a date falls in, so the board always starts on Monday. */
function weekStart(iso: string): string {
  const date = new Date(`${iso}T00:00:00Z`)
  const day = (date.getUTCDay() + 6) % 7
  date.setUTCDate(date.getUTCDate() - day)
  return date.toISOString().slice(0, 10)
}

function addDays(iso: string, days: number): string {
  const date = new Date(`${iso}T00:00:00Z`)
  date.setUTCDate(date.getUTCDate() + days)
  return date.toISOString().slice(0, 10)
}

const SPANS = { week: 7, fortnight: 14 } as const
type Span = keyof typeof SPANS

export function CrewBoard({ me }: { me: MeResponse }) {
  const [params, setParams] = useSearchParams()
  const today = new Date().toISOString().slice(0, 10)

  const from = params.get('from') ?? weekStart(today)
  const span = (params.get('span') as Span) ?? 'week'
  const to = addDays(from, SPANS[span] - 1)
  /*
   * `?job=<id>` — the job page's **Schedule** (`docs/job-stages.md` §5): the
   * Schedule focused on one job, its visits still to book in the queue and
   * the first of them picked, so the fits beside the board are its. The
   * board itself is every crew's, as always: its booked days are on it.
   */
  const focusJob = params.get('job')

  const [view, setView] = useState<BoardView | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<QueueItem | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [rainDay, setRainDay] = useState<string | null>(null)

  const editable = me.permissions.schedule === 'edit' || me.permissions.schedule === 'full'
  const pickedFor = useRef<string | null>(null)

  const load = useCallback(async () => {
    try {
      const query = new URLSearchParams({ from, to })
      const payload = await request<BoardView>(`/schedule/board?${query.toString()}`)
      setView(payload)
      setError(null)
      // Keep the selection only while it is still unscheduled.
      setSelected((current) => (current
        ? payload.queue.find((item) => item.workItemId === current.workItemId) ?? null
        : null))
      /* Focused on a job: its first visit to book is picked once, on arrival. */
      if (focusJob && pickedFor.current !== focusJob) {
        pickedFor.current = focusJob
        const first = payload.queue.find((item) => item.jobId === focusJob)
        if (first) setSelected(first)
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The board could not load.')
    }
  }, [from, to, focusJob])

  useEffect(() => { void load() }, [load])

  /*
   * Someone else's change lands here. Reloading the whole board rather than
   * patching it: the queue, the capacity bars and the day headers all move
   * together, and a partial update is how two people end up looking at numbers
   * that do not add up.
   */
  useEffect(() => {
    const stream = new EventSource('/api/v1/events')
    const reload = () => { void load() }
    for (const type of ['visits.scheduled', 'visits.moved', 'visits.rescheduled',
      'visits.skipped']) {
      stream.addEventListener(type, reload)
    }
    return () => stream.close()
  }, [load])

  const move = (next: string) =>
    setParams((current) => {
      const updated = new URLSearchParams(current)
      updated.set('from', next)
      return updated
    }, { replace: true })

  async function run(work: () => Promise<unknown>, done?: string) {
    setBusy(true)
    try {
      await work()
      await load()
      if (done) setNotice(done)
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : 'That did not work.')
    } finally {
      setBusy(false)
    }
  }

  const onDrop = (target: { crewId: string; date: string }, payload: DragPayload) => {
    if (payload.kind === 'visit') {
      return run(async () => {
        const result = await request<{ warning: { bookedHours: number; capacityHours: number } | null }>(
          `/schedule/visits/${payload.visitId}/move`,
          { method: 'POST', body: { crewId: target.crewId, date: target.date } })
        if (result.warning) {
          setNotice(`Moved — that day is now ${formatNumber(result.warning.bookedHours)} h `
            + `on ${formatNumber(result.warning.capacityHours)} h.`)
        }
      })
    }

    // Dropping a queue item places all of its remaining hours on that day.
    // Over capacity is a warning, not a refusal (§6.5).
    return run(async () => {
      const result = await request<{ warnings: { bookedHours: number; capacityHours: number }[] }>(
        '/schedule/visits',
        {
          method: 'POST',
          body: {
            workItemId: payload.workItemId,
            allocations: [{ crewId: target.crewId, date: target.date, hours: payload.hours }],
          },
        })
      setNotice(result.warnings.length > 0
        ? `Scheduled — that day is now ${formatNumber(result.warnings[0]!.bookedHours)} h `
          + `on ${formatNumber(result.warnings[0]!.capacityHours)} h.`
        : 'Scheduled.')
    })
  }

  const visitsOn = useMemo(
    () => (date: string) => (view?.visits ?? []).filter((visit) => visit.date === date),
    [view])

  /* The queue, or — focused on a job — that job's part of it, counted the same way. */
  const queue = useMemo(() => {
    if (!view) return null
    if (!focusJob) return { items: view.queue, totals: view.queueTotals }
    const items = view.queue.filter((item) => item.jobId === focusJob)
    return {
      items,
      totals: {
        items: items.length,
        hours: items.reduce((sum, item) => sum + item.remainingHours, 0),
        overdue: items.filter((item) => item.overdueDays > 0).length,
      },
    }
  }, [view, focusJob])

  if (error) {
    return (
      <EmptyState title="The board could not load" body={error}
        action={{ label: 'Try again', onClick: () => void load() }} />
    )
  }
  if (!view) return <p className="p-4 text-cell text-text-muted">Loading…</p>

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PageBar
        title="Schedule"
        sub={`${formatNumber(view.queueTotals.hours)} h waiting`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={() => move(addDays(from, -SPANS[span]))} aria-label="Previous">
              <IconChevronLeft size={15} />
            </Button>
            <span className="tnum min-w-[150px] text-center text-cell font-semibold text-text">
              {range(from, to)}
            </span>
            <Button onClick={() => move(addDays(from, SPANS[span]))} aria-label="Next">
              <IconChevronRight size={15} />
            </Button>
            <Button onClick={() => move(weekStart(today))}>Today</Button>
            <Segmented
              label="Span"
              value={span}
              onChange={(next) => setParams((current) => {
                const updated = new URLSearchParams(current)
                updated.set('span', next)
                return updated
              }, { replace: true })}
              options={[
                { value: 'week' as const, label: 'Week' },
                { value: 'fortnight' as const, label: '2 weeks' },
              ]}
            />
          </div>
        }
      />

      <div className="grid min-h-0 flex-1 grid-cols-[minmax(240px,300px)_1fr_minmax(0,0px)] transition-[grid-template-columns] lg:grid-cols-[minmax(240px,300px)_1fr_minmax(280px,340px)]">
        <div className="min-h-0 border-r border-line bg-surface">
          <UnscheduledQueue
            items={queue?.items ?? view.queue}
            totals={queue?.totals ?? view.queueTotals}
            selectedId={selected?.workItemId ?? null}
            onSelect={setSelected}
            editable={editable}
          />
        </div>

        <div className="min-h-0 overflow-y-auto">
          <CrewGrid
            crews={view.crews}
            visits={view.visits}
            dates={view.dates}
            editable={editable}
            onDrop={onDrop}
            onVisitClick={(visit) => setRainDay(visit.date)}
          />
        </div>

        <div className="min-h-0 border-l border-line bg-surface">
          {selected
            ? (
              <BestFitPanel
                item={selected}
                from={from}
                to={to}
                editable={editable}
                busy={busy}
                onClose={() => setSelected(null)}
                onAssign={(fit: BestFit) => run(async () => {
                  // A fit that cannot hold all of it places what it can; the
                  // rest stays in the queue rather than overfilling the day.
                  const hours = Math.min(selected.remainingHours, fit.openHours)
                  await request('/schedule/visits', {
                    method: 'POST',
                    body: {
                      workItemId: selected.workItemId,
                      allocations: [{ crewId: fit.crewId, date: fit.date, hours }],
                      partial: hours < selected.remainingHours,
                    },
                  })
                }, 'Scheduled.')}
              />
            )
            : (
              <div className="p-3">
                <p className="m-0 text-cell text-text-muted">
                  Pick something from the queue to see which crews fit it.
                </p>
              </div>
            )}
        </div>
      </div>

      {rainDay && (
        <RainDayModal
          date={rainDay}
          visits={visitsOn(rainDay)}
          onClose={() => setRainDay(null)}
          busy={busy}
          onSubmit={(visitIds, byDays, reason, skip) => run(async () => {
            const result = await request<{ moved: number; failed: { message: string }[] }>(
              '/schedule/reschedule',
              { method: 'POST', body: { visitIds, byDays, reason, skip } })
            setRainDay(null)
            setNotice(result.failed.length === 0
              ? `${formatNumber(result.moved)} ${skip ? 'skipped' : 'moved'}.`
              : `${formatNumber(result.moved)} done · ${result.failed.length} could not: `
                + result.failed[0]!.message)
          })}
        />
      )}

      {notice && (
        <div className="fixed bottom-4 left-1/2 z-50 -translate-x-1/2">
          <Toast onDismiss={() => setNotice(null)}>{notice}</Toast>
        </div>
      )}
    </div>
  )
}

/**
 * Rain-day recovery — §6.5's "moves a set of visits with reasons".
 *
 * Opened from a day, pre-selecting that day's work, because that is how the
 * decision is actually made: it is raining on Wednesday, so Wednesday moves.
 */
function RainDayModal({
  date, visits, onClose, busy, onSubmit,
}: {
  date: string
  visits: BoardVisit[]
  onClose: () => void
  busy: boolean
  onSubmit: (visitIds: string[], byDays: number, reason: string, skip: boolean) => void
}) {
  const movable = visits.filter((visit) =>
    !['completed', 'cancelled', 'skipped'].includes(visit.status))
  const [chosen, setChosen] = useState<string[]>(movable.map((visit) => visit.id))
  const [byDays, setByDays] = useState(1)
  const [reason, setReason] = useState('')
  const [skip, setSkip] = useState(false)

  return (
    <Modal
      open
      onClose={onClose}
      title={`${weekdayLong(date)} — move or skip`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={busy || chosen.length === 0 || !reason.trim()}
            onClick={() => onSubmit(chosen, byDays, reason.trim(), skip)}
          >
            {skip
              ? `Skip ${chosen.length}`
              : `Move ${chosen.length} by ${byDays} ${byDays === 1 ? 'day' : 'days'}`}
          </Button>
        </>
      }
    >
      {movable.length === 0
        ? <p className="m-0 text-cell text-text-muted">Nothing on this day can be moved.</p>
        : (
          <>
            <p className="m-0 mb-2 text-cell text-text-muted">
              {movable.length} {movable.length === 1 ? 'visit' : 'visits'} on this day.
            </p>

            <div className="mb-3 max-h-[180px] overflow-y-auto rounded-card border border-line">
              {movable.map((visit) => (
                <label
                  key={visit.id}
                  className="flex items-center gap-2 border-b border-line-soft px-2 py-1.5 last:border-b-0"
                >
                  <input
                    type="checkbox"
                    checked={chosen.includes(visit.id)}
                    onChange={(event) => setChosen((current) => (event.target.checked
                      ? [...current, visit.id]
                      : current.filter((id) => id !== visit.id)))}
                  />
                  <span className="min-w-0 flex-1 truncate text-cell text-text">
                    {visit.propertyName} · {formatNumber(Number(visit.hoursForDay))} h
                  </span>
                </label>
              ))}
            </div>

            <Segmented
              label="What to do"
              value={skip ? 'skip' : 'move'}
              onChange={(value) => setSkip(value === 'skip')}
              options={[
                { value: 'move' as const, label: 'Move them' },
                { value: 'skip' as const, label: 'Skip them' },
              ]}
            />

            {!skip && (
              <label className="mt-3 block">
                <span className="mb-1 block text-sub text-text-muted">Push by</span>
                <input
                  type="number"
                  min={1}
                  max={30}
                  value={byDays}
                  onChange={(event) => setByDays(Math.max(1, Number(event.target.value) || 1))}
                  className="tnum h-9 w-[80px] rounded-field border border-line bg-surface px-2 text-cell text-text"
                />
                <span className="ml-2 text-sub text-text-muted">days</span>
              </label>
            )}

            <label className="mt-3 block">
              <span className="mb-1 block text-sub text-text-muted">
                Why? Customers see this as a rescheduled visit.
              </span>
              <textarea
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                rows={2}
                placeholder="Rain 80% — pushed to Friday"
                className="w-full rounded-field border border-line bg-surface p-2 text-cell text-text placeholder:text-text-faint"
              />
            </label>
          </>
        )}
    </Modal>
  )
}

const range = (from: string, to: string): string => {
  const options = { month: 'short', day: 'numeric', timeZone: 'UTC' } as const
  const start = new Date(`${from}T00:00:00Z`).toLocaleDateString('en-US', options)
  const end = new Date(`${to}T00:00:00Z`).toLocaleDateString('en-US',
    { ...options, year: 'numeric' })
  return `${start} – ${end}`
}

const weekdayLong = (iso: string): string => {
  const date = parseDateOnly(iso)
  return date
    ? date.toLocaleDateString('en-US',
      { weekday: 'long', month: 'short', day: 'numeric', timeZone: 'UTC' })
    : iso
}
